<template>
  <div>
    <div
      v-if="loading"
      class="text-center loading"
    >
      <span class="icon icon-spinner"></span>
    </div>

    <!-- Search -->
    <div class="filter-bar">
      <div class="flex items-center margin-bottom-sm">
        <select
          v-model="category"
          class="tb-select w-auto margin-right-md"
          data-entity="producer-shoppable-categoryFilter"
        >
          <option
            v-for="(v, k) in categories"
            :key="k"
            :value="v"
          >
            {{ k }}
          </option>
        </select>

        <form
          class="tb-form-group flex-grow margin-bottom-none"
          @submit.prevent="keywordSearch()"
        >
          <div class="shoppable-search">
            <i class="icon icon-magnifier"></i>
            <input
              v-model="keyword"
              type="text"
              class="tb-input"
              placeholder="Search"
              :disabled="loading"
              ref="searchInput"
              data-entity="producer-shoppable-searchInput"
            >
            <button
              v-if="keyword"
              class="shoppable-search__clear"
              type="button"
              data-entity="producer-shoppable-clearSearch"
              @click="clearSearch"
            >
              <span class="fa fa-close"></span>
            </button>
          </div>
  
          <button
            type="submit"
            :disabled="loading || !keyword"
            class="tb-btn tb-btn-contained medium-em margin-left-md"
            data-entity="producer-shoppable-searchButton"
          >
            Search
          </button>
        </form>
      </div>

      <!-- Filters -->
      <div class="flex">
        <div class="shoppable-filters">
          <div class="toggle-switch margin-bottom-none">
            <label class="toggle-label" for="primeFilter">
              <img :src="primeLogoPath" alt="Amazon Prime Logo" class="prime-logo">
              <span class="toggle-slider-wrapper">
                <input
                  type="checkbox"
                  id="primeFilter"
                  v-model="primeFilter"
                  true-value="amazon:prime-eligible"
                  data-entity="producer-shoppable-primeFilter"
                  class="tgb-sr-only"
                />
                <span class="toggle-slider"></span>
              </span>
            </label>
          </div>
        </div>

        <div class="shoppable-filters">
          <select
            v-model="genderFilter"
            class="tb-select w-auto"
            data-entity="producer-shoppable-genderFilter"
          >
            <option value="">All Genders</option>
            <option v-for="g in genders" :key="g" :value="g">{{ g }}</option>
          </select>
        </div>
        <div class="shoppable-filters">
          <select
            v-model="reviewFilter"
            class="tb-select w-auto"
            data-entity="producer-shoppable-genderFilter"
          >
            <option :value="null">All Reviews</option>
            <option v-for="(v,k) in reviews" :key="k" :value="v">{{ k }}</option>
          </select>
        </div>

        <div class="shoppable-filters amazon-price-filter-wrap">
          <p class="shoppable-filters__title">Price</p>

          <span class="text-xxs text-os-medium">${{ priceLow }}</span>
          <div class="tb-range-slider tb-range-slider--round margin-left-xxs margin-right-xxs">
            <input
              class="tb-range-slider__input"
              type="range"
              :min="0"
              :step="25"
              :max="maxPrice"
              v-model.number="priceLow"
              data-entity="producer-shoppable-lowPriceFilter"
            />

            <input
              class="tb-range-slider__input"
              type="range"
              :min="0"
              :step="25"
              :max="maxPrice"
              v-model.number="priceHigh"
              data-entity="producer-shoppable-highPriceFilter"
            />
          </div>
          <span class="text-xxs text-os-medium">${{ priceHigh }}{{ priceHigh === maxPrice ? '+' : '' }}</span>
        </div>
      </div>
    </div>

    <!-- Results -->
    <div v-show="!loading" class="margin-top-xl shoppable-results">
      <template v-if="results.length">
        <p class="text-os-low text-xs text-right">{{ this.total }} results found</p>
        <div
          v-for="r in results"
          :key="r.sourceId"
          class="shoppable-result"
          :class="{
            'shoppable-result': true,
          }"
        >
          <img
            :src="r.imageUrl"
            class="shoppable-result__image"
            alt=""
          >
          <div class="shoppable-result__info">
            <p class="shoppable-result__details">
              {{ r.price }} {{ r.currency }} 
              <span>(ID: {{ r.sourceId }})</span>
              <img
                v-if="r.sourceItem.Offers?.Listings[0]?.DeliveryInfo?.IsPrimeEligible || primeFilter"
                :src="primeLogoPath"
                alt="Amazon Prime Logo"
              >
            </p>
            <p
              class="shoppable-result__name ellipsis"
            >
              <a
                :href="r.sourceUrl"
                target="_blank"
                class="shoppable-result__link text-os-medium"
              >
                <span class="tgb-sr-only">Link to product page</span>
                <span class="fa fa-external-link"></span>
                <span>{{ r.name }}</span>
              </a>
            </p>
          </div>
          <button
            class="tb-btn tb-btn-contained medium-em"
            :class="{
              'high-em tb-btn-contained--override-disabled': savedItemLookup[r.sourceId],
            }"
            :disabled="savedItemLookup[r.sourceId]"
            :data-test="`producer-shoppable-selectProduct-${r.sourceId}`"
            @click="chooseItem(r)"
          >
            {{ savedItemLookup[r.sourceId] ? 'Saved' : 'Select' }}
          </button>
        </div>
        <div
          v-if="this.total > 10 && this.page < this.pages"
          class="shoppable-load-more"
        >
          <button
            type="submit"
            :disabled="loading"
            class="tb-btn tb-btn-contained medium-em shoppable-load-more__button"
            data-entity="producer-shoppable-loadMore"
            @click="loadMore"
          >
            Load More
          </button>
        </div>
      </template>

      <div
        v-else-if="noResults"
        class="text-os-low text-xs text-center"
      >
        <p class="text-bold">
          No products found.
        </p>
        <p>
          Check your spelling or try filtering for better results.
        </p>
      </div>
      <p
        v-else
        class="text-os-low text-xs text-center"
      >Search a product to get started</p>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash-es';

import CatalogMixin from '@/js/mixins/shoppable-catalog';

export default {
  name: 'AmazonCatalog',
  mixins: [CatalogMixin],
  data() {
    return {
      limit: 10, // override limit from mixin
      category: 'All',
      categories: {
        'All Departments': 'All',
        'Baby': 'Baby',
        'Beauty & Personal Care': 'Beauty',
        'Clothing, Shoes & Jewelry': 'Fashion',
        'Electronics': 'Electronics',
        'Health, Household & Baby Care': 'HealthPersonalCare',
        'Home & Kitchen': 'HomeAndKitchen',
        'Office Products': 'OfficeProducts',
        'Pet Supplies': 'PetSupplies',
        'Sports & outdoors': 'SportsAndOutdoors',
        'Toys & games': 'ToysAndGames',
      },
      primeFilter: false,
      genderFilter: '',
      genders: [
        "Men's", "Women's", "Kid's", 'Baby',
      ],
      reviewFilter: null,
      reviews: {
        '4+ Stars': 'rating:4',
        '3+ Stars': 'rating:3',
        '2+ Stars': 'rating:2',
        '1+ Stars': 'rating:1',
      },
      priceLow: 0,
      priceHigh: 100,
      maxPrice: 100,
    };
  },

  computed: {
    priceRange() {
      if (this.priceHigh === this.maxPrice && this.priceLow === 0) {
        return '';
      } 
      
      if (this.priceHigh === this.maxPrice) {
        return `price:${this.priceLow}-`;
      }

      return `price:${this.priceLow}-${this.priceHigh}`;
    },
    allFilters() {
      return [
        this.reviewFilter,
        this.primeFilter,
        this.priceRange,
      ].filter(Boolean);
    },
    searchQuery() {
      return `${this.keyword} ${this.genderFilter}`.trim();
    },
    queryParams() {
      return {
        filters: this.allFilters.join(','),
        category: this.category,
      };
    },
    primeLogoPath() {
      return `${process.env.ASSET_CDN_BASE}/logos/amazon/prime-logo.svg`;
    },
  },

  created() {
    this.store = 'amazon'; // set data for mixin
  },
  mounted() {
    this.$refs.searchInput.focus();
  },

  watch: {
    allFilters: debounce(function() {
      this.keywordSearch();
    }, 500),
    priceLow(v) {
      if (v > this.priceHigh) {
        this.priceHigh = v;
      }
    },
    priceHigh(v) {
      if (v < this.priceLow) {
        this.priceLow = v;
      }
    },
  },
};
</script>

<style lang="scss">
.amazon-price-filter-wrap {
  flex-basis: 30%;
}

.toggle-switch {
  display: flex;
  align-items: center;
}

.toggle-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 0;
}

.prime-logo {
  margin-right: 8px;
  height: 15px;
}

.toggle-slider-wrapper {
  position: relative;
  width: 27px;
  height: 15px;
}

.toggle-slider {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #969696;
  border-radius: 15px; 
  transition: 0.4s;
}

.toggle-slider:before {
  content: "";
  position: absolute;
  height: 11px;
  width: 11px;
  left: 2px; 
  bottom: 2px;
  background-color: white;
  border-radius: 50%;
  transition: 0.4s;
}

input:checked + .toggle-slider {
  background-color: #18C2ED; 
}

input:checked + .toggle-slider:before {
  transform: translateX(12px); 
}
</style>
